// =====================================================================================
//  ⚠️  TEST-ONLY UI — DELETE / KEEP DISABLED IN PRODUCTION  ⚠️
// -------------------------------------------------------------------------------------
//  A floating dev panel for testing specific Power-Up ("Pup") cards without grinding the
//  Exchange stage, and for stacking specific hole / community / deck cards to set up
//  hand+pup combinations. It talks ONLY to the backend /testpup/** endpoints, which are
//  themselves gated by the `pup.testing.enabled` backend property — so even if this panel
//  is left on, it is inert unless the backend harness is also enabled.
//
//  TOGGLE: flip TESTING_ENABLED below. When false the panel renders nothing and makes no
//  network calls. Leave it false (the committed default) for the real product; set it true
//  only for local testing. See CLAUDE.md → "Testing specific pup / hole cards".
// =====================================================================================
import React, { useState } from "react";
import { postWithToken } from "../../utilities/fetchWithToken";
import { BASE_URL } from "../../utilities/BASE_URL";
import "./TestPupPanel.css";

// Master on/off switch for the whole test panel. KEEP false in the shipped product.
export const TESTING_ENABLED = false;

// The 26 gold codes, for the quick-pick datalist (any silver/celestial code also works).
const GOLD_CODES = [
  "SHRINKIFY", "UNEXPECTED_GROWTH", "PREMONITION", "RAGE", "RUN_IT_TWICE",
  "SACRIFICIAL_CEREMONY", "SAVIOR", "SHORT_KING", "SHUT_DOWN", "SOCIAL_DISTANCING",
  "SPY_MASTER", "STRAIGHT_UP_CONNECTED", "SUBSTITUTION", "SUPER_SUIT",
  "SUPPLEMENTAL_STRENGTH", "WILD_CHILD", "WORTHY_SACRIFICE", "JACKPOT", "JUICING",
  "ONE_SHORT", "PEACEFUL_WANDERER", "BUTTON_UP", "COUNTER", "GOOD_ECONOMY",
  "GRAND_EXCHANGE", "HALF_BLIND_BANDIT",
];

export default function TestPupPanel({ roomId, mySeat }) {
  const [code, setCode] = useState("SHRINKIFY");
  const [hole, setHole] = useState("");
  const [community, setCommunity] = useState("");
  const [deckHead, setDeckHead] = useState("");
  const [seat, setSeat] = useState("");
  const [status, setStatus] = useState("");
  const [collapsed, setCollapsed] = useState(false);

  if (!TESTING_ENABLED) return null;

  const targetSeat = seat.trim() === "" ? undefined : Number(seat);

  const call = async (path, extra, label) => {
    try {
      const res = await postWithToken(`${BASE_URL}/testpup/${path}`, { roomId, ...extra });
      const text = await res.text();
      if (!res.ok) {
        setStatus(`❌ ${label}: ${res.status} ${text}`);
        return;
      }
      setStatus(`✅ ${label}: ${text}`);
    } catch (e) {
      setStatus(`❌ ${label}: ${e.message} (is the backend harness enabled?)`);
    }
  };

  return (
    <div className="testpup-panel">
      <div className="testpup-title" onClick={() => setCollapsed((c) => !c)}>
        🧪 PUP TEST HARNESS (room {roomId} · seat {mySeat}) {collapsed ? "▸" : "▾"}
      </div>
      {!collapsed && (
        <div className="testpup-body">
          <label className="testpup-row">
            <span>Seat (blank = me)</span>
            <input value={seat} onChange={(e) => setSeat(e.target.value)} placeholder={String(mySeat ?? "")} />
          </label>

          <label className="testpup-row">
            <span>Pup code</span>
            <input list="testpup-gold" value={code} onChange={(e) => setCode(e.target.value)} />
          </label>
          <datalist id="testpup-gold">
            {GOLD_CODES.map((c) => <option key={c} value={c} />)}
          </datalist>
          <button onClick={() => call("grantpup", { code: code.trim().toUpperCase(), seat: targetSeat }, "grant")}>
            Grant pup card
          </button>

          <label className="testpup-row">
            <span>Hole cards</span>
            <input value={hole} onChange={(e) => setHole(e.target.value)} placeholder="Ah Kh" />
          </label>
          <button onClick={() => call("sethole", { cards: hole, seat: targetSeat }, "sethole")}>
            Set hole cards
          </button>

          <label className="testpup-row">
            <span>Community</span>
            <input value={community} onChange={(e) => setCommunity(e.target.value)} placeholder="2h 5h 9h Kd" />
          </label>
          <button onClick={() => call("setcommunity", { cards: community }, "setcommunity")}>
            Set community
          </button>

          <label className="testpup-row">
            <span>Deck top</span>
            <input value={deckHead} onChange={(e) => setDeckHead(e.target.value)} placeholder="Ah Ad" />
          </label>
          <button onClick={() => call("setdeckhead", { cards: deckHead }, "setdeckhead")}>
            Stack deck top
          </button>

          <button onClick={() => call("dumpstate", {}, "state")}>Dump state → status</button>

          {status && <div className="testpup-status">{status}</div>}
        </div>
      )}
    </div>
  );
}
